import { useMemo, type ReactNode } from 'react';
import { DndProvider as ReactDndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import type { DndProviderProps } from './types';

/**
 * Detects if the current device supports touch events.
 */
function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-expect-error - msMaxTouchPoints is IE-specific
    navigator.msMaxTouchPoints > 0
  );
}

/**
 * Multi-backend provider that switches between HTML5 and Touch backends.
 * This is a simpler approach than using react-dnd-multi-backend.
 * Note: Backend is determined on initial mount and doesn't change.
 */
function MultiBackendProvider({
  children,
  forceTouchBackend = false,
}: DndProviderProps): ReactNode {
  // Determine backend once on mount - react-dnd doesn't support switching backends
  // Using useMemo to ensure the value is stable across renders
  const isTouch = useMemo(
    () => forceTouchBackend || isTouchDevice(),
    [forceTouchBackend]
  );

  // Use the appropriate backend based on device capabilities
  const backend = isTouch ? TouchBackend : HTML5Backend;

  // Touch backend options for better tablet support
  const touchOptions = useMemo(
    () =>
      isTouch
        ? {
            enableMouseEvents: true, // Allow mouse events on touch devices
            delayTouchStart: 0, // No delay - touch-action CSS prevents scroll
            ignoreContextMenu: true, // Ignore context menu on long press
          }
        : undefined,
    [isTouch]
  );

  return (
    <ReactDndProvider backend={backend} options={touchOptions}>
      {children}
    </ReactDndProvider>
  );
}

/**
 * DnD Provider component that wraps the application to enable drag and drop.
 * Automatically detects touch devices and uses the appropriate backend.
 *
 * @example
 * ```tsx
 * <DndProvider>
 *   <YourApp />
 * </DndProvider>
 * ```
 */
export function DndProvider({
  children,
  forceTouchBackend,
}: DndProviderProps): ReactNode {
  return (
    <MultiBackendProvider forceTouchBackend={forceTouchBackend}>
      {children}
    </MultiBackendProvider>
  );
}
